import { useEffect, useState } from "react";
import { Camera, Copy, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Btn, Spinner } from "@/components/rp";
import { Section } from "@/components/commesse";
import { mediaUrl } from "@/components/admin/fineLavori";
import { CompareSlider } from "@/components/CompareSlider";

export const diarioImg = (r) => fileUrl(`/api/diario/${r.eid}/photos/${r.pid}`);

const Grid = ({ children, testid }) => <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={testid}>{children}</div>;

const Prima = ({ oid, d, setD }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { setD((await api.post(`/orders/${oid}/foto-prima`, fd)).data); toast.success("Foto prima dei lavori salvate"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const fromSopr = async () => { try { setD((await api.post(`/orders/${oid}/foto-prima/da-sopralluogo`)).data); toast.success("Foto importate dal sopralluogo"); } catch (e) { toast.error(errMsg(e)); } };
  const setAmb = async (p, ambiente) => { try { setD((await api.patch(`/orders/${oid}/foto-prima/${p.id}`, { ambiente })).data); toast.success("Ambiente salvato"); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (p) => { if (!window.confirm("Eliminare questa foto?")) return; try { setD((await api.delete(`/orders/${oid}/foto-prima/${p.id}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section label={`Foto prima dei lavori (${d.prima.length})`} testid="foto-prima-section">
      <div className="flex flex-wrap gap-2 mb-3">
        <label className={`rp-btn bg-black text-white cursor-pointer h-10 ${busy ? "opacity-60" : ""}`} data-testid="foto-prima-upload"><Camera className="h-4 w-4" /> Aggiungi foto<input type="file" multiple accept="image/*" className="hidden" onChange={upload} disabled={busy} data-testid="foto-prima-input" /></label>
        {d.sopralluogo_photos > 0 && <Btn variant="outline" className="h-10" onClick={fromSopr} data-testid="foto-prima-from-sopr"><Download className="h-4 w-4" /> Importa dal sopralluogo ({d.sopralluogo_photos})</Btn>}
      </div>
      {!d.prima.length && <p className="text-sm text-[#757575]">Scatta lo stato di partenza di ogni ambiente: il cliente le vede nel suo cantiere e a fine lavori diventano la galleria prima e dopo.</p>}
      <Grid testid="foto-prima-grid">
        {d.prima.map((p) => (
          <div key={p.id} className="relative group" data-testid={`foto-prima-${p.id}`}>
            <img src={mediaUrl(oid, p)} alt={p.filename} loading="lazy" className="aspect-square w-full rounded-xl object-cover bg-[#F1EDE6]" />
            <button onClick={() => del(p)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" aria-label="Elimina" data-testid={`foto-prima-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            <input defaultValue={p.ambiente || ""} onBlur={(e) => e.target.value !== (p.ambiente || "") && setAmb(p, e.target.value)} className="rp-field h-8 mt-1 text-xs px-2" placeholder="Ambiente" data-testid={`foto-prima-ambiente-${p.id}`} />
          </div>
        ))}
      </Grid>
    </Section>
  );
};

const Dopo = ({ oid, d, setD }) => {
  const [refs, setRefs] = useState(d.dopo_refs);
  const on = (p) => refs.some((r) => r.pid === p.pid);
  const toggle = (p) => setRefs(on(p) ? refs.filter((r) => r.pid !== p.pid) : refs.length >= 12 ? refs : [...refs, { eid: p.eid, pid: p.pid }]);
  const save = async () => { try { setD((await api.put(`/orders/${oid}/prima-dopo`, { dopo_refs: refs })).data); toast.success("Galleria aggiornata"); } catch (e) { toast.error(errMsg(e)); } };
  const copy = () => { navigator.clipboard.writeText(d.link); toast.success("Link copiato"); };
  return (
    <Section label="Galleria prima e dopo" testid="prima-dopo-section">
      <p className="text-xs text-[#757575] mb-3">Scegli dal Diario di bordo le foto «dopo» (massimo 12). Se non scegli, usiamo quelle del verbale o le ultime del diario. Il link parte in automatico con la richiesta di recensione.</p>
      {!d.diario_photos.length ? <p className="text-sm text-[#757575]">Nessuna foto condivisa nel diario.</p> : (
        <Grid testid="dopo-picker">
          {d.diario_photos.map((p) => (
            <button key={p.pid} type="button" onClick={() => toggle(p)} className={`aspect-square rounded-xl overflow-hidden transition-opacity ${on(p) ? "ring-2 ring-black" : "opacity-50 hover:opacity-100"}`} data-testid={`dopo-photo-${p.pid}`}>
              <img src={diarioImg(p)} alt={p.filename} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </Grid>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Btn variant="secondary" className="h-10" onClick={save} data-testid="prima-dopo-save">Salva foto «dopo» ({refs.length})</Btn>
        {d.link ? (
          <>
            <a href={d.link} target="_blank" rel="noreferrer" className="text-sm underline" data-testid="prima-dopo-open">Apri la galleria</a>
            <Btn variant="outline" className="h-10" onClick={copy} data-testid="prima-dopo-copy"><Copy className="h-4 w-4" /> Copia link</Btn>
          </>
        ) : <span className="text-xs text-[#757575]" data-testid="prima-dopo-missing">Il link si attiva quando ci sono foto prima e dopo.</span>}
      </div>
    </Section>
  );
};

const Pairs = ({ oid, d, setD }) => {
  const [pairs, setPairs] = useState(d.pairs);
  const [sel, setSel] = useState(null);
  const [amb, setAmb] = useState("");
  const used = new Set(pairs.map((x) => x.prima_id));
  const pick = (p) => {
    setPairs([...pairs, { prima_id: sel, eid: p.eid, pid: p.pid, ambiente: amb.trim() || primaById[sel]?.ambiente || "" }]);
    setSel(null); setAmb("");
  };
  const save = async () => { try { setD((await api.put(`/orders/${oid}/prima-dopo`, { dopo_refs: d.custom ? d.dopo_refs : [], pairs })).data); toast.success("Confronti salvati"); } catch (e) { toast.error(errMsg(e)); } };
  const primaById = Object.fromEntries(d.prima.map((p) => [p.id, p]));
  const sugg = d.suggestions.filter((s) => !used.has(s.prima_id) && !pairs.some((x) => x.pid === s.pid));
  const accept = (list) => setPairs([...pairs, ...list.map(({ prima_id, eid, pid, ambiente }) => ({ prima_id, eid, pid, ambiente }))]);
  return (
    <Section label={`Confronto per ambiente (${pairs.length})`} testid="pairs-section">
      <p className="text-xs text-[#757575] mb-3">Scrivi l'ambiente sotto ogni foto «prima»: se lo stesso ambiente compare nel diario ti proponiamo noi l'abbinamento. Oppure: 1. tocca una foto «prima» e scrivi l'ambiente, 2. tocca la sua foto «dopo». Ricorda di salvare.</p>
      {sugg.length > 0 && (
        <div className="rounded-2xl border border-[#C8BDAB] bg-[#E6DDCE]/40 p-4 mb-4" data-testid="pair-suggestions">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <p className="text-sm font-medium flex-1">Abbinamenti proposti in automatico ({sugg.length}): l'ambiente della foto «prima» compare nel diario</p>
            <Btn className="h-9" onClick={() => accept(sugg)} data-testid="pair-accept-all">Accetta tutti</Btn>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sugg.map((s) => primaById[s.prima_id] && (
              <div key={s.prima_id} className="space-y-2" data-testid={`pair-suggestion-${s.prima_id}`}>
                <CompareSlider before={mediaUrl(oid, primaById[s.prima_id])} after={diarioImg(s)} label={`${s.ambiente} · diario del ${s.date.split("-").reverse().join("/")}`} testid={`sugg-compare-${s.prima_id}`} />
                <Btn variant="outline" className="h-8 text-xs" onClick={() => accept([s])} data-testid={`pair-accept-${s.prima_id}`}>Accetta</Btn>
              </div>
            ))}
          </div>
        </div>
      )}
      <Grid testid="pairs-prima">
        {d.prima.filter((p) => !used.has(p.id)).map((p) => (
          <button key={p.id} type="button" onClick={() => setSel(sel === p.id ? null : p.id)} className={`aspect-square rounded-xl overflow-hidden ${sel === p.id ? "ring-2 ring-black" : sel ? "opacity-40" : ""}`} data-testid={`pair-prima-${p.id}`}>
            <img src={mediaUrl(oid, p)} alt={p.filename} loading="lazy" className="h-full w-full object-cover bg-[#F1EDE6]" />
          </button>
        ))}
      </Grid>
      {sel && (
        <div className="mt-3 space-y-3 rounded-2xl bg-[#F7F7F7] p-3" data-testid="pair-picker">
          <input value={amb} onChange={(e) => setAmb(e.target.value)} className="rp-field bg-white" placeholder="Ambiente (es. Bagno)" data-testid="pair-ambiente" />
          <p className="text-xs text-[#757575]">Ora tocca la foto «dopo»:</p>
          <Grid testid="pair-dopo-picker">{d.diario_photos.map((p) => <button key={p.pid} type="button" onClick={() => pick(p)} className="aspect-square rounded-xl overflow-hidden hover:ring-2 hover:ring-black" data-testid={`pair-dopo-${p.pid}`}><img src={diarioImg(p)} alt={p.filename} loading="lazy" className="h-full w-full object-cover bg-[#F1EDE6]" /></button>)}</Grid>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {pairs.map((x, i) => primaById[x.prima_id] && (
          <div key={x.prima_id} className="relative" data-testid={`pair-${i}`}>
            <CompareSlider before={mediaUrl(oid, primaById[x.prima_id])} after={diarioImg(x)} label={x.ambiente || "Ambiente"} testid={`pair-compare-${i}`} />
            <button onClick={() => setPairs(pairs.filter((_, j) => j !== i))} className="absolute top-2 left-1/2 -translate-x-1/2 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center" aria-label="Rimuovi abbinamento" data-testid={`pair-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <Btn variant="secondary" className="h-10 mt-4" onClick={save} data-testid="pairs-save">Salva confronti</Btn>
    </Section>
  );
};

export const PrimaDopo = ({ oid }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/orders/${oid}/prima-dopo`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [oid]);
  if (!d) return <Spinner />;
  return <div className="space-y-6" data-testid="prima-dopo-panel"><Prima oid={oid} d={d} setD={setD} /><Pairs key={`p-${d.pairs.map((x) => x.pid).join()}-${d.prima.length}-${d.suggestions.length}`} oid={oid} d={d} setD={setD} /><Dopo key={d.dopo_refs.map((r) => r.pid).join()} oid={oid} d={d} setD={setD} /></div>;
};

export const ClientPrima = ({ oid }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/client/orders/${oid}/prima-dopo`).then((r) => setD(r.data)).catch(() => {}); }, [oid]);
  if (!d || !d.prima.length) return null;
  return (
    <Section label="Com'era prima dei lavori" testid="client-foto-prima">
      <Grid testid="client-foto-prima-grid">{d.prima.map((p) => <a key={p.id} href={mediaUrl(oid, p)} target="_blank" rel="noreferrer"><img src={mediaUrl(oid, p)} alt={p.filename} loading="lazy" className="aspect-square w-full rounded-xl object-cover" /></a>)}</Grid>
      {d.link && <a href={d.link} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm underline" data-testid="client-prima-dopo-link">Guarda la galleria prima e dopo</a>}
    </Section>
  );
};
